import { Box, Flex, MantineTheme, Select, Text, TextInput } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { Button as DesignButton } from 'src/design-system/Button';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { useAdvisors } from '../hooks/useAdvisors';

type Props = {
  title: string;
  familyName: string;
  onFamilyNameChange: (value: string) => void;
  onCancel: () => void;
  showAdvisorPicker?: boolean;
  advisorUserId?: string;
  onAdvisorChange?: (value: string | null) => void;
};

const pillInputStyles = (theme: MantineTheme, focused: boolean) => ({
  height: 48,
  borderRadius: 9999,
  backgroundColor: theme.white,
  border: `2px solid ${focused ? theme.colors.brand[6] : theme.colors.gray[2]}`,
  fontSize: theme.fontSizes.md,
});

function AdvisorPickerField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string | null) => void;
}) {
  const { advisors } = useAdvisors();
  const options = advisors.map(advisor => ({
    value: advisor.id,
    label: formatName(advisor.firstName, advisor.lastName, NameFormat.NATURAL),
  }));

  return (
    <Box style={{ flex: 1 }}>
      <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.6" mb={6}>
        Adviser
      </Text>
      <Select
        data={options}
        value={value || null}
        onChange={onChange}
        radius="xl"
        size="md"
        comboboxProps={{ withinPortal: true }}
        styles={theme => ({
          wrapper: { height: 48 },
          input: pillInputStyles(theme, false),
        })}
      />
    </Box>
  );
}

export default function FamilyNameHeader({
  title,
  familyName,
  onFamilyNameChange,
  onCancel,
  showAdvisorPicker,
  advisorUserId,
  onAdvisorChange,
}: Props) {
  const [focused, setFocused] = useState(false);
  const hasName = familyName.trim().length > 0;

  return (
    <Box px="xl" pt="xxl" pb="md">
      <Flex justify="space-between" align="center" pb="lg">
        <Text c="gray.10" size="displaySm" fw={FONT_WEIGHTS.semibold}>
          {title}
        </Text>
        <DesignButton variant="text" style={{ flexShrink: 0 }} onClick={onCancel}>
          Cancel
        </DesignButton>
      </Flex>

      <Flex gap="md" align="flex-start">
        <Box style={{ flex: 1 }}>
          <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.6" mb={6}>
            Family name
          </Text>
          <TextInput
            placeholder="Family name"
            value={familyName}
            onChange={e => onFamilyNameChange(e.currentTarget.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            rightSection={
              hasName ? (
                <IconCheck size={18} color="var(--mantine-color-green-6)" />
              ) : (
                <IconX size={18} color="var(--mantine-color-red-6)" />
              )
            }
            radius="xl"
            size="md"
            styles={theme => ({
              wrapper: { height: 48 },
              input: pillInputStyles(theme, focused),
              section: { width: 44, justifyContent: 'center' },
            })}
          />
        </Box>

        {showAdvisorPicker && onAdvisorChange && (
          <AdvisorPickerField value={advisorUserId ?? ''} onChange={onAdvisorChange} />
        )}
      </Flex>
    </Box>
  );
}
